import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  addSupplement,
  approveContext,
  approveTechnicalScope,
  getContext,
  cancelRun,
  createRun,
  getDeployStatus,
  getPackage,
  getPackageDrafting,
  getPackageRun,
  getRun,
  isRunTerminal,
  pauseRun,
  resumeRun,
  getTechnicalScope,
  listActivity,
  listArtifacts,
  listChangeSets,
  listDevelopers,
  listFlags,
  listPackages,
  listSupplements,
  getChangeSetObject,
  getCorrections,
  getEscalations,
  getEvidence,
  getEvidenceEvent,
  getGateHistory,
  getManifests,
  getObjectGraph,
  getParity,
  getTransportHistory,
  getReadyGate,
  markReady,
  rebuildContext,
  resolveFlag,
  seedBoard,
  setAssignee,
  setStatus,
} from "@/api/code-companion";
import type { CcPackageStatus } from "@/api/code-companion";

// Query keys centralizadas para invalidação consistente.
const keys = {
  packages: (p: string) => ["cc", "packages", p] as const,
  package: (p: string, id: number) => ["cc", "package", p, id] as const,
  activity: (p: string) => ["cc", "activity", p] as const,
  flags: (p: string) => ["cc", "flags", p] as const,
  developers: (p: string) => ["cc", "developers", p] as const,
  artifacts: (p: string, id: number) => ["cc", "artifacts", p, id] as const,
  changeSets: (p: string, id: number) => ["cc", "change-sets", p, id] as const,
  deployStatus: (p: string, csId: number) => ["cc", "deploy-status", p, csId] as const,
  drafting: (p: string, id: number) => ["cc", "drafting", p, id] as const,
};

export function useCcPackages(projectId: string | undefined) {
  return useQuery({
    queryKey: keys.packages(projectId ?? ""),
    queryFn: () => listPackages(projectId!),
    enabled: !!projectId,
  });
}

export function useCcPackage(projectId: string | undefined, packageId: number | undefined) {
  return useQuery({
    queryKey: keys.package(projectId ?? "", packageId ?? 0),
    queryFn: () => getPackage(projectId!, packageId!),
    enabled: !!projectId && !!packageId,
  });
}

export function useCcActivity(projectId: string | undefined, limit = 100) {
  return useQuery({
    queryKey: keys.activity(projectId ?? ""),
    queryFn: () => listActivity(projectId!, { limit }),
    enabled: !!projectId,
  });
}

export function useCcFlags(
  projectId: string | undefined,
  status: "open" | "resolved" | "all" = "open",
) {
  return useQuery({
    queryKey: [...keys.flags(projectId ?? ""), status],
    queryFn: () => listFlags(projectId!, status),
    enabled: !!projectId,
  });
}

export function useCcDevelopers(projectId: string | undefined) {
  return useQuery({
    queryKey: keys.developers(projectId ?? ""),
    queryFn: () => listDevelopers(projectId!),
    enabled: !!projectId,
  });
}

export function useCcArtifacts(projectId: string | undefined, packageId: number | undefined) {
  return useQuery({
    queryKey: keys.artifacts(projectId ?? "", packageId ?? 0),
    queryFn: () => listArtifacts(projectId!, packageId!),
    enabled: !!projectId && !!packageId,
  });
}

export function useCcContext(projectId: string | undefined, packageId: number | undefined) {
  return useQuery({
    queryKey: ["cc", "context", projectId ?? "", packageId ?? 0],
    queryFn: () => getContext(projectId!, packageId!),
    enabled: !!projectId && !!packageId,
    retry: false, // 404 quando o contexto ainda não foi montado
  });
}

export function useCcSeed(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => seedBoard(projectId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.packages(projectId) }),
  });
}

export function useCcTechnicalScope(projectId: string | undefined) {
  return useQuery({
    queryKey: ["cc", "technical-scope", projectId ?? ""],
    queryFn: () => getTechnicalScope(projectId!),
    enabled: !!projectId,
  });
}

export function useCcApproveTechnicalScope(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => approveTechnicalScope(projectId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cc", "technical-scope", projectId] });
      qc.invalidateQueries({ queryKey: keys.packages(projectId) });
    },
  });
}

export function useCcApproveContext(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (packageId: number) => approveContext(projectId, packageId),
    onSuccess: (_, packageId) => {
      qc.invalidateQueries({ queryKey: keys.packages(projectId) });
      qc.invalidateQueries({ queryKey: keys.package(projectId, packageId) });
      qc.invalidateQueries({ queryKey: keys.activity(projectId) });
    },
  });
}

export function useCcSetAssignee(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ packageId, userId }: { packageId: number; userId: string | null }) =>
      setAssignee(projectId, packageId, userId),
    onSuccess: (_, { packageId }) => {
      qc.invalidateQueries({ queryKey: keys.packages(projectId) });
      qc.invalidateQueries({ queryKey: keys.package(projectId, packageId) });
      qc.invalidateQueries({ queryKey: keys.activity(projectId) });
    },
  });
}

/** Um evento do dossiê, com o cru. Só busca quando alguém abre (#513). */
export function useCcEvidenceEvent(
  projectId: string,
  packageId: number | undefined,
  eventId: number | undefined,
) {
  return useQuery({
    queryKey: [...keys.package(projectId, packageId ?? 0), "evidence", eventId ?? 0],
    queryFn: () => getEvidenceEvent(projectId, packageId as number, eventId as number),
    // `enabled` é o ponto: sem ele a lista inteira buscaria o cru de cada linha, que é
    // exatamente o peso que tirar o `sap_raw` da lista veio resolver.
    enabled: !!projectId && !!packageId && !!eventId,
    staleTime: 5 * 60_000,
  });
}

export function useCcEvidence(projectId: string, packageId: number | undefined) {
  return useQuery({
    queryKey: [...keys.package(projectId, packageId ?? 0), "evidence"],
    queryFn: () => getEvidence(projectId, packageId as number),
    enabled: !!projectId && !!packageId,
    // O tail existe para responder "algo se moveu agora?" ao lado de uma matriz que
    // repolla. Sem intervalo próprio ele congela enquanto o run anda, que é o oposto
    // do que ele promete.
    refetchInterval: 10_000,
  });
}

/**
 * A narrativa da autocorreção do change set (S-064 onda 1).
 *
 * Fica na chave do change set, e não do pacote: o mesmo pacote tem vários change sets
 * e misturar as tentativas de dois deles contaria a história errada.
 */
export function useCcCorrections(
  projectId: string,
  changeSetId: number | undefined,
) {
  return useQuery({
    queryKey: ["cc", projectId, "change-set", changeSetId ?? 0, "corrections"],
    queryFn: () => getCorrections(projectId, changeSetId as number),
    enabled: !!projectId && !!changeSetId,
  });
}

/** O manifesto (todas as versões) dos objetos do pacote — a decisão da migração. */
export function useCcManifests(projectId: string, packageId: number | undefined) {
  return useQuery({
    queryKey: [...keys.package(projectId, packageId ?? 0), "manifests"],
    queryFn: () => getManifests(projectId, packageId as number),
    enabled: !!projectId && !!packageId,
  });
}

/**
 * Um objeto do change set com os dois sources e o diff.
 *
 * `enabled` só com objeto escolhido: o source pode ter milhares de linhas e buscar
 * todos de uma vez para mostrar um seria carregar o pacote inteiro por engano.
 */
export function useCcChangeSetObject(
  projectId: string,
  changeSetId: number | undefined,
  objectName: string | undefined,
  objectType?: string,
) {
  return useQuery({
    // O tipo entra na chave: em pilha RAP o mesmo nome tem dois objetos, e sem ele um
    // sobrescreveria o cache do outro.
    // eslint-disable-next-line -- chave longa por identidade composta, não por descuido
    queryKey: ["cc", projectId, "change-set", changeSetId ?? 0, "object", objectName ?? "", objectType ?? ""],
    queryFn: () =>
      getChangeSetObject(projectId, changeSetId as number, objectName as string, objectType),
    enabled: !!projectId && !!changeSetId && !!objectName,
  });
}

/** As avaliações do gate: quando foi pedido, o que respondeu e sobre quais objetos. */
export function useCcGateHistory(projectId: string, packageId: number | undefined) {
  return useQuery({
    queryKey: [...keys.package(projectId, packageId ?? 0), "gate-history"],
    queryFn: () => getGateHistory(projectId, packageId as number),
    enabled: !!projectId && !!packageId,
  });
}

/** Os casos de paridade do pacote: o código novo faz o mesmo que o antigo? */
export function useCcParity(projectId: string, packageId: number | undefined) {
  return useQuery({
    queryKey: [...keys.package(projectId, packageId ?? 0), "parity"],
    queryFn: () => getParity(projectId, packageId as number),
    enabled: !!projectId && !!packageId,
  });
}

/**
 * O que o run perguntou ao humano, e o que ele respondeu.
 *
 * Revalida junto do run: uma escalação nasce quando o run pausa, e a lista precisa
 * refletir isso sem esperar o usuário navegar.
 */
export function useCcEscalations(projectId: string, runId: string | undefined) {
  return useQuery({
    queryKey: ["cc", projectId, "run", runId ?? "", "escalations"],
    queryFn: () => getEscalations(projectId, runId as string),
    enabled: !!projectId && !!runId,
    refetchInterval: 15_000,
  });
}

/** O grafo do pacote: arestas, ordem de build e ordem de rollback. */
export function useCcGraph(projectId: string, packageId: number | undefined) {
  return useQuery({
    queryKey: [...keys.package(projectId, packageId ?? 0), "graph"],
    queryFn: () => getObjectGraph(projectId, packageId as number),
    enabled: !!projectId && !!packageId,
  });
}

/** Todos os eventos de transporte do pacote, não só o último estado de cada um. */
export function useCcTransport(projectId: string, packageId: number | undefined) {
  return useQuery({
    queryKey: [...keys.package(projectId, packageId ?? 0), "transport"],
    queryFn: () => getTransportHistory(projectId, packageId as number),
    enabled: !!projectId && !!packageId,
  });
}

export function useCcReadyGate(projectId: string, packageId: number | undefined) {
  return useQuery({
    queryKey: [...keys.package(projectId, packageId ?? 0), "ready-gate"],
    queryFn: () => getReadyGate(projectId, packageId as number),
    enabled: !!projectId && !!packageId,
  });
}

export function useCcMarkReady(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      packageId,
      deployLocation,
    }: {
      packageId: number;
      deployLocation: string;
    }) => markReady(projectId, packageId, deployLocation),
    onSuccess: (_, { packageId }) => {
      qc.invalidateQueries({ queryKey: keys.packages(projectId) });
      qc.invalidateQueries({ queryKey: keys.package(projectId, packageId) });
      qc.invalidateQueries({ queryKey: keys.activity(projectId) });
    },
  });
}

export function useCcSetStatus(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ packageId, status }: { packageId: number; status: CcPackageStatus }) =>
      setStatus(projectId, packageId, status),
    onSuccess: (_, { packageId }) => {
      qc.invalidateQueries({ queryKey: keys.packages(projectId) });
      qc.invalidateQueries({ queryKey: keys.package(projectId, packageId) });
      qc.invalidateQueries({ queryKey: keys.activity(projectId) });
    },
  });
}

export function useCcResolveFlag(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (flagId: number) => resolveFlag(projectId, flagId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.flags(projectId) });
      qc.invalidateQueries({ queryKey: keys.activity(projectId) });
    },
  });
}

// ── Curadoria de contexto (CC.6) ─────────────────────────────────────────────
const supplementsKey = (p: string, id: number) =>
  ["cc", "supplements", p, id] as const;

export function useCcSupplements(projectId: string | undefined, packageId: number | undefined) {
  return useQuery({
    queryKey: supplementsKey(projectId ?? "", packageId ?? 0),
    queryFn: () => listSupplements(projectId!, packageId!),
    enabled: !!projectId && !!packageId,
  });
}

export function useCcAddSupplement(projectId: string, packageId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title: string; body: string; flag_id?: number }) =>
      addSupplement(projectId, packageId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: supplementsKey(projectId, packageId) });
      qc.invalidateQueries({ queryKey: keys.flags(projectId) });
    },
  });
}

export function useCcRebuildContext(projectId: string, packageId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => rebuildContext(projectId, packageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cc", "context", projectId, packageId] });
      qc.invalidateQueries({ queryKey: supplementsKey(projectId, packageId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Trace (CC.31 fatia 1)
// ---------------------------------------------------------------------------

/**
 * Change sets do pacote.
 *
 * `live` liga o polling enquanto um run está em curso. **É o conserto de um buraco
 * real**: desde a CC.34 F2 o run monta o próprio change set, e sem repetir a pergunta a
 * tela ficaria para sempre no estado "este pacote não tem change set" enquanto o worker
 * enche um. O usuário veria o run rodando e a matriz vazia, e a única saída seria
 * recarregar a página.
 *
 * Sem run vivo não há polling: change set não nasce sozinho, e bater no banco numa tela
 * aberta e esquecida é custo sem informação.
 */
export function useCcChangeSets(
  projectId: string | undefined,
  packageId: number | undefined,
  opts?: { live?: boolean },
) {
  return useQuery({
    queryKey: keys.changeSets(projectId ?? "", packageId ?? 0),
    queryFn: () => listChangeSets(projectId!, packageId!),
    enabled: !!projectId && !!packageId,
    refetchInterval: opts?.live ? 6000 : false,
  });
}

/**
 * O roster de drafting do pacote: o que virou source, o que falta, e o que espera um dev.
 *
 * O polling segue a mesma regra dos outros: só enquanto há run vivo. O roster é
 * durável e não muda sozinho — quem o mexe é o worker.
 */
export function useCcDrafting(
  projectId: string | undefined,
  packageId: number | undefined,
  opts?: { live?: boolean },
) {
  return useQuery({
    queryKey: keys.drafting(projectId ?? "", packageId ?? 0),
    queryFn: () => getPackageDrafting(projectId!, packageId!),
    enabled: !!projectId && !!packageId,
    refetchInterval: opts?.live ? 6000 : false,
  });
}

/**
 * Rollup de fases do change set.
 *
 * `refetchInterval` só enquanto houver objeto pendente: um deploy avança em
 * rajadas (segundos para escrever, minutos para uma correção por LLM) e boa parte
 * do trabalho acontece **fora do navegador**, no terminal do dev via MCP. Parar de
 * pedir quando tudo concluiu evita bater no banco à toa numa tela aberta e
 * esquecida.
 */
export function useCcDeployStatus(
  projectId: string | undefined,
  changeSetId: number | undefined,
) {
  return useQuery({
    queryKey: keys.deployStatus(projectId ?? "", changeSetId ?? 0),
    queryFn: () => getDeployStatus(projectId!, changeSetId!),
    enabled: !!projectId && !!changeSetId,
    refetchInterval: (query) => (query.state.data?.all_concluded ? false : 8000),
  });
}

/**
 * Estado do run, com polling que **para sozinho** quando o run termina.
 *
 * Run pausado continua sendo consultado: a pausa pode ser resolvida por outra pessoa
 * (reconectar o ADT, responder o HITL), e parar de perguntar deixaria a tela mentindo
 * que ainda está parado depois de já ter voltado.
 */
export function useCcRun(projectId: string | undefined, runId: string | undefined) {
  return useQuery({
    queryKey: ["cc", "run", projectId, runId],
    queryFn: () => getRun(projectId!, runId!),
    enabled: !!projectId && !!runId,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s && isRunTerminal(s) ? false : 5000;
    },
  });
}

/** Run corrente do pacote. Alimenta o botão de disparar e o painel na tela de detalhes. */
export function useCcPackageRun(projectId: string | undefined, packageId: number | undefined) {
  return useQuery({
    queryKey: ["cc", "package-run", projectId, packageId],
    queryFn: () => getPackageRun(projectId!, packageId!),
    enabled: !!projectId && !!packageId,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s && !isRunTerminal(s) ? 5000 : false;
    },
  });
}

/** Dispara o run do pacote. Invalida o estado para o painel aparecer na hora. */
export function useCcCreateRun(projectId: string | undefined, packageId: number | undefined) {
  const qc = useQueryClient();
  return useMutation({
    /**
     * `retry` reexecuta o que já rodou; `only` recorta a objetos. São ortogonais, e o
     * botão "tentar de novo" combina os dois quando é um objeto só.
     */
    mutationFn: (opts?: {
      retry?: boolean;
      only?: { object_name: string; object_type: string }[];
      change_set_id?: number;
    }) => createRun(projectId!, packageId!, opts ?? {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cc", "package-run", projectId, packageId] });
      // CC.34 F2: disparar passou a **criar** change set e a semear o roster de
      // drafting. Sem invalidar os dois, a tela segue mostrando o estado de antes do
      // clique — nenhum change set e nenhum objeto — enquanto o worker trabalha.
      if (projectId && packageId) {
        qc.invalidateQueries({ queryKey: keys.changeSets(projectId, packageId) });
        qc.invalidateQueries({ queryKey: keys.drafting(projectId, packageId) });
      }
    },
  });
}

/**
 * Controles do run. Cada um invalida o estado para o painel refletir na hora, sem
 * esperar o próximo ciclo de polling: quem clica precisa de resposta imediata, senão
 * clica de novo achando que não funcionou.
 */
function useRunAction<T>(
  projectId: string | undefined,
  runId: string | undefined,
  fn: (p: string, r: string, arg?: T) => Promise<unknown>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (arg?: T) => fn(projectId!, runId!, arg),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cc", "run", projectId, runId] });
      qc.invalidateQueries({ queryKey: ["cc", "package-run", projectId] });
    },
  });
}

export function useCcPauseRun(projectId?: string, runId?: string) {
  return useRunAction<string>(projectId, runId, (p, r, reason) =>
    pauseRun(p, r, reason ?? "manual"),
  );
}

export function useCcResumeRun(projectId?: string, runId?: string) {
  return useRunAction<Record<string, unknown>>(projectId, runId, (p, r, answer) =>
    resumeRun(p, r, answer),
  );
}

export function useCcCancelRun(projectId?: string, runId?: string) {
  return useRunAction<void>(projectId, runId, (p, r) => cancelRun(p, r));
}
